import React, { useEffect, useState, useCallback } from 'react';
import { getActiveStaff, getAttendanceByDate, markAttendance, attendanceAPI, companyAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const MONTH_NAMES_FULL = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function haversine(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function StaffAttendanceView() {
  const { user } = useAuth();
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const [officeBranch, setOfficeBranch] = useState(null);

  useEffect(() => {
    companyAPI.getBranches().then(r => {
      const branches = (r.data || []).filter(b => b.latitude && b.longitude);
      const hq = branches.find(b => b.isHeadquarter) || branches[0] || null;
      setOfficeBranch(hq);
    }).catch(() => {});
  }, []);

  const STATUS_CFG = {
    PRESENT:  { color: '#10b981', bg: '#d1fae5', label: 'Present' },
    ABSENT:   { color: '#ef4444', bg: '#fee2e2', label: 'Absent' },
    HALF_DAY: { color: '#f59e0b', bg: '#fef3c7', label: 'Half Day' },
    LEAVE:    { color: '#3b82f6', bg: '#dbeafe', label: 'Leave' },
    HOLIDAY:  { color: '#8b5cf6', bg: '#ede9fe', label: 'Holiday' },
  };

  const loadRecords = () => {
    if (!user?.staffId) return;
    setLoading(true);
    attendanceAPI.getMonthly(user.staffId, month, year)
      .then(r => { setRecords(r.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { loadRecords(); }, [user?.staffId, month, year]);

  const counts = records.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});

  const prevMonth = () => { if (month === 1) { setMonth(12); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => {
    const isCurrentMonth = month === (now.getMonth() + 1) && year === now.getFullYear();
    if (isCurrentMonth) return;
    if (month === 12) { setMonth(1); setYear(y => y + 1); } else setMonth(m => m + 1);
  };

  const isCurrentMonth = month === (now.getMonth() + 1) && year === now.getFullYear();
  const todayStr = now.toISOString().split('T')[0];
  const todayRecord = records.find(r => r.date === todayStr);
  const alreadyPresent = todayRecord?.status === 'PRESENT';

  const handleCheckIn = () => {
    if (!officeBranch) return toast.error('Office location not set. Ask admin to set GPS in Company → Branches.');
    if (!navigator.geolocation) return toast.error('Geolocation not supported by your browser');
    setCheckingIn(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const dist = haversine(pos.coords.latitude, pos.coords.longitude, officeBranch.latitude, officeBranch.longitude);
        if (dist > 200) {
          toast.error(`You are ${Math.round(dist)}m from ${officeBranch.name}. Must be within 200m to check in.`);
          setCheckingIn(false);
          return;
        }
        const rightNow = new Date();
        const checkIn = rightNow.toTimeString().slice(0, 5);
        attendanceAPI.markBulk({
          date: rightNow.toISOString().split('T')[0],
          entries: [{ staffId: user.staffId, status: 'PRESENT', checkIn, remarks: `GPS check-in (${officeBranch.name})` }],
        })
          .then(() => { toast.success(`Checked in at ${checkIn}`); loadRecords(); })
          .catch(() => toast.error('Check-in failed. Please contact HR.'))
          .finally(() => setCheckingIn(false));
      },
      () => { toast.error('Location access denied. Enable GPS to check in.'); setCheckingIn(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>My Attendance</div>
          <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 2 }}>Your personal attendance record</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {isCurrentMonth && (
            <button
              onClick={handleCheckIn}
              disabled={checkingIn || alreadyPresent}
              style={{
                padding: '10px 20px', borderRadius: 10, border: 'none', cursor: checkingIn || alreadyPresent ? 'not-allowed' : 'pointer',
                background: alreadyPresent ? '#d1fae5' : 'linear-gradient(135deg, #059669, #10b981)',
                color: alreadyPresent ? '#065f46' : '#fff',
                fontWeight: 700, fontSize: 13,
                boxShadow: alreadyPresent ? 'none' : '0 4px 14px rgba(5,150,105,0.35)',
                opacity: checkingIn ? 0.7 : 1,
              }}
            >
              {checkingIn ? '📍 Locating...' : alreadyPresent ? '✓ Checked In' : '📍 Check In'}
            </button>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff', borderRadius: 12, padding: '8px 16px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <button onClick={prevMonth} style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>
            <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', minWidth: 130, textAlign: 'center' }}>{MONTH_NAMES_FULL[month - 1]} {year}</span>
            <button onClick={nextMonth} style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: isCurrentMonth ? 0.3 : 1 }}>›</button>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 24, flexWrap: 'wrap' }}>
        {Object.entries(STATUS_CFG).map(([key, cfg]) => (
          <div key={key} style={{ background: cfg.bg, borderRadius: 14, padding: '16px 20px', flex: 1, minWidth: 100, textAlign: 'center' }}>
            <div style={{ fontSize: 26, fontWeight: 800, color: cfg.color }}>{counts[key] || 0}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: cfg.color, marginTop: 4 }}>{cfg.label}</div>
          </div>
        ))}
      </div>

      {/* Records */}
      <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', fontWeight: 700, fontSize: 14, color: '#0f172a' }}>
          {records.length} Records — {MONTH_NAMES_FULL[month - 1]} {year}
        </div>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>Loading...</div>
        ) : records.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>📋</div>
            <div style={{ fontWeight: 600 }}>No records for this month</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {records.sort((a, b) => b.date.localeCompare(a.date)).map((r, i) => {
              const cfg = STATUS_CFG[r.status] || STATUS_CFG.ABSENT;
              const d = new Date(r.date + 'T00:00:00');
              return (
                <div key={r.id} style={{
                  display: 'flex', alignItems: 'center', gap: 16,
                  padding: '12px 20px', borderBottom: '1px solid #f8fafc',
                  background: i % 2 === 0 ? '#fff' : '#fafbff',
                  borderLeft: `4px solid ${cfg.color}`,
                }}>
                  <div style={{ minWidth: 50, textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a' }}>{d.getDate()}</div>
                    <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>{d.toLocaleDateString('en-IN', { weekday: 'short' }).toUpperCase()}</div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <span style={{ background: cfg.bg, color: cfg.color, padding: '4px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>{cfg.label}</span>
                    {r.remarks && <span style={{ marginLeft: 12, fontSize: 12, color: '#94a3b8' }}>— {r.remarks}</span>}
                  </div>
                  <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'right' }}>
                    {r.checkIn && <div>In: <b style={{ color: '#0f172a' }}>{r.checkIn}</b></div>}
                    {r.checkOut && <div>Out: <b style={{ color: '#0f172a' }}>{r.checkOut}</b></div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function CalendarView({ onSelectDate }) {
  const today = new Date();
  const [calYear, setCalYear]   = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth());
  const [selected, setSelected] = useState(today.toISOString().split('T')[0]);
  const [dayData, setDayData]   = useState({});
  const [loading, setLoading]   = useState(false);

  const loadDay = useCallback(async (dateStr) => {
    if (dayData[dateStr] !== undefined) return;
    setLoading(true);
    try {
      const r = await getAttendanceByDate(dateStr);
      const records = r.data || [];
      const present = records.filter(rec => rec.status === 'PRESENT').length;
      const absent  = records.filter(rec => rec.status === 'ABSENT').length;
      const leave   = records.filter(rec => rec.status === 'LEAVE').length;
      setDayData(prev => ({ ...prev, [dateStr]: { present, absent, leave, total: records.length } }));
    } catch { setDayData(prev => ({ ...prev, [dateStr]: null })); }
    finally { setLoading(false); }
  }, [dayData]);

  const selectDay = (dateStr) => {
    setSelected(dateStr);
    loadDay(dateStr);
    onSelectDate(dateStr);
  };

  const prevMonth = () => { if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); } else setCalMonth(m => m - 1); };
  const nextMonth = () => { if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); } else setCalMonth(m => m + 1); };

  const firstDay  = new Date(calYear, calMonth, 1).getDay();
  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const todayStr = today.toISOString().split('T')[0];

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const data = dayData[selected];

  return (
    <div>
      {/* Calendar header */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <button onClick={prevMonth} style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, width: 34, height: 34, cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>
          <div style={{ fontWeight: 800, fontSize: 16, color: '#0f172a' }}>{MONTH_NAMES[calMonth]} {calYear}</div>
          <button onClick={nextMonth} style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, width: 34, height: 34, cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>
        </div>

        {/* Day headers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 8 }}>
          {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
            <div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#94a3b8', padding: '4px 0' }}>{d}</div>
          ))}
        </div>

        {/* Day cells */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
          {cells.map((day, i) => {
            if (!day) return <div key={i} />;
            const m = String(calMonth + 1).padStart(2, '0');
            const dd = String(day).padStart(2, '0');
            const dateStr = `${calYear}-${m}-${dd}`;
            const isToday = dateStr === todayStr;
            const isSelected = dateStr === selected;
            const hasFuture = dateStr > todayStr;
            const dd2 = dayData[dateStr];

            return (
              <div key={i}
                onClick={() => !hasFuture && selectDay(dateStr)}
                style={{
                  borderRadius: 10, padding: '8px 4px', textAlign: 'center', cursor: hasFuture ? 'default' : 'pointer',
                  background: isSelected ? '#1a237e' : isToday ? '#eff6ff' : '#f8fafc',
                  border: `2px solid ${isSelected ? '#1a237e' : isToday ? '#bfdbfe' : 'transparent'}`,
                  transition: 'all 0.15s', opacity: hasFuture ? 0.35 : 1,
                }}
                onMouseEnter={e => { if (!hasFuture && !isSelected) e.currentTarget.style.background = '#e0e7ff'; }}
                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = isToday ? '#eff6ff' : '#f8fafc'; }}
              >
                <div style={{ fontSize: 13, fontWeight: isSelected || isToday ? 700 : 500, color: isSelected ? '#fff' : isToday ? '#1d4ed8' : '#374151', marginBottom: 3 }}>{day}</div>
                {dd2 && dd2.total > 0 ? (
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981' }} title={`Present: ${dd2.present}`} />
                    {dd2.absent > 0 && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#ef4444' }} title={`Absent: ${dd2.absent}`} />}
                    {dd2.leave > 0 && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#3b82f6' }} title={`Leave: ${dd2.leave}`} />}
                  </div>
                ) : <div style={{ height: 6 }} />}
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected day detail */}
      {selected && (
        <div style={{ background: '#fff', borderRadius: 16, padding: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', marginBottom: 12 }}>
            {new Date(selected + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </div>
          {loading ? (
            <div style={{ color: '#94a3b8', fontSize: 13 }}>Loading attendance...</div>
          ) : data ? data.total > 0 ? (
            <div style={{ display: 'flex', gap: 16 }}>
              {[
                { label: 'Present', count: data.present, color: '#10b981', bg: '#d1fae5' },
                { label: 'Absent', count: data.absent, color: '#ef4444', bg: '#fee2e2' },
                { label: 'Leave', count: data.leave, color: '#3b82f6', bg: '#dbeafe' },
                { label: 'Total', count: data.total, color: '#64748b', bg: '#f1f5f9' },
              ].map(s => (
                <div key={s.label} style={{ background: s.bg, borderRadius: 12, padding: '12px 20px', textAlign: 'center', flex: 1 }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.count}</div>
                  <div style={{ fontSize: 11, color: s.color, fontWeight: 600, marginTop: 2 }}>{s.label}</div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: '#94a3b8', fontSize: 13 }}>No attendance records for this date. Click a date to load data.</div>
          ) : (
            <div style={{ color: '#94a3b8', fontSize: 13 }}>Click any past date to view attendance summary.</div>
          )}
        </div>
      )}
    </div>
  );
}

const S = {
  PRESENT:  { label: 'Present',  short: 'P',   color: '#10b981', bg: '#d1fae5', border: '#6ee7b7' },
  ABSENT:   { label: 'Absent',   short: 'A',   color: '#ef4444', bg: '#fee2e2', border: '#fca5a5' },
  HALF_DAY: { label: 'Half Day', short: 'H/D', color: '#f59e0b', bg: '#fef3c7', border: '#fcd34d' },
  LEAVE:    { label: 'Leave',    short: 'L',   color: '#3b82f6', bg: '#dbeafe', border: '#93c5fd' },
  HOLIDAY:  { label: 'Holiday',  short: 'HOL', color: '#8b5cf6', bg: '#ede9fe', border: '#c4b5fd' },
};

function initials(name) {
  return name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';
}

const AVATAR_COLORS = ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#ec4899','#84cc16'];

export default function Attendance() {
  const { user } = useAuth();
  if (user?.role !== 'ADMIN') return <StaffAttendanceView />;

  return <AdminAttendanceView />;
}

function AdminAttendanceView() {
  const [tab, setTab] = useState('mark');
  const [staff, setStaff] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [records, setRecords] = useState({});
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');

  useEffect(() => {
    getActiveStaff().then(r => {
      setStaff(r.data || []);
      const init = {};
      (r.data || []).forEach(s => { init[s.id] = { status: 'PRESENT', remarks: '' }; });
      setRecords(init);
    });
  }, []);

  useEffect(() => {
    if (!date) return;
    getAttendanceByDate(date).then(r => {
      const existing = {};
      (r.data || []).forEach(a => { existing[a.staff.id] = { status: a.status, remarks: a.remarks || '' }; });
      setRecords(prev => {
        const merged = { ...prev };
        Object.keys(existing).forEach(id => { merged[id] = existing[id]; });
        return merged;
      });
    }).catch(() => {});
  }, [date]);

  const setStatus = (id, status) => setRecords(prev => ({ ...prev, [id]: { ...prev[id], status } }));
  const setRemarks = (id, remarks) => setRecords(prev => ({ ...prev, [id]: { ...prev[id], remarks } }));
  const markAll = (status) => {
    const u = {};
    staff.forEach(s => { u[s.id] = { status, remarks: '' }; });
    setRecords(u);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const entries = Object.entries(records).map(([staffId, rec]) => ({
        staffId: parseInt(staffId), status: rec.status, remarks: rec.remarks,
      }));
      await markAttendance({ date, entries });
      toast.success('Attendance saved!');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const counts = Object.values(records).reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});
  const q = search.trim().toLowerCase();
  const statusFiltered = filter === 'ALL' ? staff : staff.filter(s => records[s.id]?.status === filter);
  const shown = q
    ? statusFiltered.filter(s =>
        s.name?.toLowerCase().includes(q) ||
        s.employeeCode?.toLowerCase().includes(q) ||
        s.department?.name?.toLowerCase().includes(q)
      )
    : statusFiltered;

  return (
    <div>
      {/* Tab Bar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {[{ key: 'mark', label: '✅ Mark Attendance' }, { key: 'calendar', label: '📅 Calendar View' }].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '9px 22px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
            background: tab === t.key ? '#1a237e' : '#e2e8f0',
            color: tab === t.key ? '#fff' : '#64748b', transition: 'all 0.15s',
          }}>{t.label}</button>
        ))}
      </div>

      {tab === 'calendar' && <CalendarView onSelectDate={(d) => { setDate(d); setTab('mark'); }} />}

      {tab === 'mark' && <>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Mark Attendance</div>
          <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 2 }}>{staff.length} employees</div>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{
            padding: '9px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0',
            fontSize: 13, background: '#fff', color: '#0f172a', fontWeight: 500,
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          }} />
          <button onClick={handleSave} disabled={saving} style={{
            padding: '10px 24px', borderRadius: 10,
            background: saving ? '#94a3b8' : 'linear-gradient(135deg, #1a237e, #1565c0)',
            color: 'white', border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
            fontWeight: 700, fontSize: 13, boxShadow: '0 4px 14px rgba(26,35,126,0.3)',
            transition: 'all 0.2s',
          }}>
            {saving ? 'Saving...' : '💾 Save Attendance'}
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 15, color: '#94a3b8', pointerEvents: 'none' }}>🔍</span>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, staff ID, or department..."
            style={{
              width: '100%', padding: '10px 16px 10px 40px', borderRadius: 12,
              border: '1.5px solid #e2e8f0', fontSize: 13, background: '#fff',
              color: '#0f172a', outline: 'none', boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
              boxSizing: 'border-box', transition: 'border-color 0.15s',
            }}
            onFocus={e => e.target.style.borderColor = '#3b82f6'}
            onBlur={e => e.target.style.borderColor = '#e2e8f0'}
          />
          {search && (
            <button onClick={() => setSearch('')} style={{
              position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
              background: '#e2e8f0', border: 'none', borderRadius: '50%',
              width: 22, height: 22, cursor: 'pointer', fontSize: 12, color: '#64748b',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700,
            }}>✕</button>
          )}
        </div>
        {search && (
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 6, paddingLeft: 4 }}>
            {shown.length} result{shown.length !== 1 ? 's' : ''} found for <b>"{search}"</b>
          </div>
        )}
      </div>

      {/* Summary + Bulk Actions */}
      <div style={{ background: '#fff', borderRadius: 16, padding: '16px 20px', marginBottom: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <div onClick={() => setFilter('ALL')} style={{
          padding: '6px 16px', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700,
          background: filter === 'ALL' ? '#0f172a' : '#f1f5f9',
          color: filter === 'ALL' ? '#fff' : '#475569',
          border: '1.5px solid transparent', transition: 'all 0.15s',
        }}>All: {staff.length}</div>
        {Object.entries(S).map(([key, cfg]) => (
          <div key={key} onClick={() => setFilter(filter === key ? 'ALL' : key)} style={{
            padding: '6px 16px', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700,
            background: filter === key ? cfg.color : cfg.bg,
            color: filter === key ? '#fff' : cfg.color,
            border: `1.5px solid ${filter === key ? cfg.color : cfg.border}`,
            transition: 'all 0.15s',
          }}>{cfg.label}: {counts[key] || 0}</div>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button onClick={() => markAll('PRESENT')} style={{
            padding: '6px 14px', borderRadius: 8, background: '#d1fae5', color: '#065f46',
            border: '1px solid #6ee7b7', cursor: 'pointer', fontSize: 11, fontWeight: 700,
          }}>✓ All Present</button>
          <button onClick={() => markAll('HOLIDAY')} style={{
            padding: '6px 14px', borderRadius: 8, background: '#ede9fe', color: '#5b21b6',
            border: '1px solid #c4b5fd', cursor: 'pointer', fontSize: 11, fontWeight: 700,
          }}>★ Holiday</button>
        </div>
      </div>

      {/* Attendance Grid */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {shown.map((s, i) => {
          const rec = records[s.id] || { status: 'ABSENT', remarks: '' };
          const cfg = S[rec.status] || S.ABSENT;
          const avatarColor = AVATAR_COLORS[i % AVATAR_COLORS.length];
          return (
            <div key={s.id} style={{
              background: '#fff', borderRadius: 14, padding: '12px 20px',
              boxShadow: '0 1px 8px rgba(0,0,0,0.05)',
              display: 'flex', alignItems: 'center', gap: 16,
              borderLeft: `4px solid ${cfg.color}`,
              transition: 'box-shadow 0.15s',
            }}>
              {/* Avatar */}
              <div style={{
                width: 40, height: 40, borderRadius: 12, background: avatarColor,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontWeight: 800, fontSize: 13, flexShrink: 0,
              }}>{initials(s.name)}</div>

              {/* Info */}
              <div style={{ minWidth: 180, flex: '0 0 180px' }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>{s.name}</div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                  {s.employeeCode} · {s.department?.name || '-'}
                </div>
              </div>

              {/* Status Buttons */}
              <div style={{ display: 'flex', gap: 6, flex: 1, flexWrap: 'wrap' }}>
                {Object.entries(S).map(([key, c]) => (
                  <button key={key} onClick={() => setStatus(s.id, key)} style={{
                    padding: '5px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                    cursor: 'pointer', transition: 'all 0.12s',
                    background: rec.status === key ? c.color : c.bg,
                    color: rec.status === key ? '#fff' : c.color,
                    border: `1.5px solid ${rec.status === key ? c.color : c.border}`,
                    transform: rec.status === key ? 'scale(1.05)' : 'scale(1)',
                  }}>{c.short}</button>
                ))}
              </div>

              {/* Status Badge */}
              <div style={{
                background: cfg.bg, color: cfg.color,
                padding: '4px 14px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                flexShrink: 0, minWidth: 80, textAlign: 'center',
              }}>{cfg.label}</div>

              {/* Remarks */}
              <input value={rec.remarks} onChange={e => setRemarks(s.id, e.target.value)}
                placeholder="Remark..."
                style={{
                  padding: '6px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0',
                  fontSize: 12, width: 160, flexShrink: 0, outline: 'none',
                  transition: 'border-color 0.15s',
                }}
                onFocus={e => e.target.style.borderColor = '#3b82f6'}
                onBlur={e => e.target.style.borderColor = '#e2e8f0'}
              />
            </div>
          );
        })}
        {shown.length === 0 && (
          <div style={{ textAlign: 'center', padding: 60, background: '#fff', borderRadius: 16, color: '#94a3b8' }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>📋</div>
            <div style={{ fontWeight: 600 }}>No records to display</div>
          </div>
        )}
      </div>
      </>}
    </div>
  );
}
